import { PropsWithChildren, ReactNode } from "react";

import dayjs from "dayjs";
import { ChevronRight } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";

/**
 * Shared building blocks for the resource show (detail) pages. They lean on the
 * IMS design tokens (muted/border/foreground) so the pages stay consistent with
 * the rest of the app while giving each resource room for a richer layout.
 */

function toDayjs(raw?: string | number | null) {
  if (raw === null || raw === undefined || raw === "") return null;
  // Admin getOne serializes DateTimeOffset to ISO strings; a numeric value is a
  // defensive fallback (unix seconds, or ms if it looks like epoch millis).
  const d =
    typeof raw === "number"
      ? dayjs.unix(raw > 1e12 ? Math.round(raw / 1000) : raw)
      : dayjs(raw);
  return d.isValid() ? d : null;
}

export function fmtDateTime(raw?: string | number | null): string {
  return toDayjs(raw)?.format("DD MMM YYYY, HH:mm") ?? "—";
}

export function fmtDate(raw?: string | number | null): string {
  return toDayjs(raw)?.format("DD MMM YYYY") ?? "—";
}

/**
 * Read-only "price checked" line under a screening's or performance's price list. Tina sets
 * `priceCheckedAt` (never a curator, never the model) each time it reads that price on the
 * source page; null means Tina never confirmed it.
 */
export function PriceCheckedAt({ value }: { value?: string | number | null }) {
  return (
    <p
      className="mt-2 text-xs text-muted-foreground"
      data-testid="price-checked-at"
    >
      Price checked by Tina: {value ? fmtDateTime(value) : "never"}
    </p>
  );
}

export function DetailSection({
  title,
  icon,
  action,
  className,
  contentClassName,
  children,
}: PropsWithChildren<{
  title: string;
  icon?: ReactNode;
  action?: ReactNode;
  className?: string;
  contentClassName?: string;
}>) {
  return (
    <Card className={className}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="flex items-center gap-2 text-base font-semibold">
          {icon}
          {title}
        </CardTitle>
        {action}
      </CardHeader>
      <CardContent className={contentClassName}>{children}</CardContent>
    </Card>
  );
}

export function DetailGrid({
  children,
  className,
}: PropsWithChildren<{ className?: string }>) {
  return (
    <dl
      className={cn(
        "grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2",
        className,
      )}
    >
      {children}
    </dl>
  );
}

export function DetailField({
  label,
  value,
  mono,
  full,
  children,
}: PropsWithChildren<{
  label: string;
  value?: ReactNode;
  mono?: boolean;
  full?: boolean;
}>) {
  const content = children ?? value;
  const isEmpty =
    content === null ||
    content === undefined ||
    content === "" ||
    content === "—";

  return (
    <div className={cn("min-w-0 space-y-1", full && "sm:col-span-2")}>
      <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd
        className={cn(
          "break-words text-sm",
          mono && "font-mono text-[13px]",
          isEmpty && "text-muted-foreground",
        )}
      >
        {isEmpty ? "—" : content}
      </dd>
    </div>
  );
}

export function EmptyHint({ children }: PropsWithChildren) {
  return <p className="text-sm text-muted-foreground">{children}</p>;
}

/** Render a string array as a row of badges, or an em dash when empty. */
export function Chips({
  items,
  variant = "secondary",
  empty = "—",
}: {
  items?: (string | null | undefined)[] | null;
  variant?: "secondary" | "outline" | "default";
  empty?: ReactNode;
}) {
  const values = (items ?? []).filter(
    (v): v is string => !!v && v.trim().length > 0,
  );
  if (!values.length)
    return <span className="text-sm text-muted-foreground">{empty}</span>;
  return (
    <div className="flex flex-wrap gap-1.5">
      {values.map((v, i) => (
        <Badge key={`${v}-${i}`} variant={variant} className="font-normal">
          {v}
        </Badge>
      ))}
    </div>
  );
}

export function RawJsonCard({ data }: { data: unknown }) {
  return (
    <Collapsible>
      <Card>
        <CollapsibleTrigger className="group flex w-full items-center gap-2 px-6 py-4 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground">
          <ChevronRight className="h-4 w-4 transition-transform group-data-[state=open]:rotate-90" />
          Raw JSON
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="pt-0">
            <pre className="max-h-[480px] overflow-auto rounded-md bg-muted p-4 font-mono text-xs leading-relaxed">
              {JSON.stringify(data, null, 2)}
            </pre>
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}
